import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { MobileHeader } from "./MobileHeader";
import { ProductSheet } from "./ProductSheet";
import { QuoteSwitcherSheet } from "./QuoteSwitcherSheet";
import BarcodeScannerSheet from "./BarcodeScannerSheet";
import { MobileFilterSheet } from "./MobileFilterSheet";
import { EMPTY_FILTERS, buildInventoryParams, activeFilterCount } from "./catalogFilters";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, CaretRight, Receipt, Package, ImageBroken, Barcode as BarcodeIcon, Funnel } from "@phosphor-icons/react";

function approvalBadge(approval, t) {
  const a = (approval || "").toLowerCase();
  if (a === "yes") return { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", text: t("mobile.approved") };
  if (a) return { cls: "bg-gray-100 text-gray-500 border-gray-200", text: t("mobile.rejected") };
  return { cls: "bg-white text-[#A8A29E] border-dashed border-[#D6D3D1]", text: t("mobile.pending") };
}

const fmtUsd = (v) => (v != null ? `$${Number(v).toFixed(2)}` : "-");
const fmtRmb = (v) => (v != null ? `¥${Number(v).toFixed(2)}` : "-");

// iter 284: per-size lines shown inline under a product in the Budget / CI views.
function VariantLines({ p, view, isAdmin, onToggleApproval, t }) {
  return (
    <div className="px-4 pb-2.5 -mt-1" data-testid={`mobile-variant-lines-${p.product_id}`}>
      <div className={`grid ${view === "budget" ? "grid-cols-[1fr_auto_auto_auto]" : "grid-cols-[1fr_auto_auto]"} gap-x-3 text-[10px] font-semibold text-[#A8A29E] uppercase tracking-wide pb-1 border-b border-[#F1F0EE]`}>
        <span>{t("mobile.size")}</span>
        {view === "budget" ? <><span className="text-right">{t("mobile.rmb_cost")}</span><span className="text-right">{t("mobile.export_price")}</span><span className="text-right">{t("mobile.approval")}</span></>
          : <><span className="text-right">{t("mobile.ci_qty")}</span><span className="text-right">{t("mobile.export_price")}</span></>}
      </div>
      {p.entries.map((e) => {
        const badge = approvalBadge(e.approval, t);
        const other = e.ci_reserved_other || 0;
        return (
          <div key={e.entry_id} className={`grid ${view === "budget" ? "grid-cols-[1fr_auto_auto_auto]" : "grid-cols-[1fr_auto_auto]"} gap-x-3 items-center py-1 border-b border-[#F7F6F4] last:border-b-0 text-xs`} data-testid={`mobile-variant-line-${e.entry_id}`}>
            <span className="text-[#44403C] truncate">{e.size || "-"}</span>
            {view === "budget" ? (
              <>
                <span className="font-mono text-right text-[#78716C]">{fmtRmb(e.purchase_price)}</span>
                <span className="font-mono text-right font-semibold text-[#1E3A8A]">{fmtUsd(e.suggested_price_usd)}</span>
                {isAdmin ? (
                  <button type="button" onClick={(ev) => { ev.stopPropagation(); onToggleApproval(e); }} data-testid={`mobile-approval-toggle-${e.entry_id}`}
                    className={`press-scale justify-self-end px-2 py-0.5 rounded-full border text-[10px] font-medium ${badge.cls}`}>{badge.text}</button>
                ) : (
                  <span className={`justify-self-end px-2 py-0.5 rounded-full border text-[10px] font-medium ${badge.cls}`} data-testid={`mobile-approval-badge-${e.entry_id}`}>{badge.text}</span>
                )}
              </>
            ) : (
              <>
                <span className="font-mono text-right" data-testid={`mobile-ci-qty-${e.entry_id}`}>
                  <span className={e.ci_inventory_qty ? "text-[#1C1917] font-semibold" : "text-[#A8A29E]"}>{e.ci_inventory_qty ?? "-"}</span>
                  {other > 0 && <span className="ml-1 text-[10px] text-amber-600">({t("mobile.ci_available")} {e.ci_available ?? e.ci_inventory_qty})</span>}
                </span>
                <span className="font-mono text-right font-semibold text-[#1E3A8A]">{fmtUsd(e.suggested_price_usd)}</span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CatalogRow({ p, onClick, t, view = "product", isAdmin, onToggleApproval }) {
  const first = p.entries[0] || {};
  return (
    <div className="border-b border-[#F1F0EE] last:border-b-0 bg-white">
    <button
      onClick={onClick}
      data-testid={`mobile-product-card-${p.product_id}`}
      className="press-scale w-full text-left flex items-center gap-3 px-4 py-2.5"
    >
      <div className="w-14 h-14 rounded-[10px] bg-[#FAFAF9] overflow-hidden flex items-center justify-center flex-shrink-0">
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.description} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <ImageBroken size={20} className="text-[#E7E5E4]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-mono text-[#78716C] truncate">{first.item_no || "-"}</p>
        <p className="text-sm text-[#1C1917] truncate leading-snug">{p.description || "-"}</p>
        <p className="text-[11px] text-[#A8A29E] mt-0.5">{t("mobile.sizes", { count: p.entries.length })}{p.sample_qty ? <span className={`ml-2 font-mono ${p.sample_qty < 0 ? "text-red-600" : "text-[#1E3A8A]"}`} data-testid={`mobile-sample-badge-${p.product_id}`}>{t("samples.short")} {p.sample_qty}</span> : null}</p>
      </div>
      <p className="text-sm font-semibold text-[#1E3A8A] flex-shrink-0 whitespace-nowrap">{priceRangeLabel(p.entries)}</p>
    </button>
    {view !== "product" && <VariantLines p={p} view={view} isAdmin={isAdmin} onToggleApproval={onToggleApproval} t={t} />}
    </div>
  );
}

const _lastCatalog = { rows: [], query: "", filters: null };
const VIEW_KEY = "mobileCatalogView";

function groupByProduct(rows) {
  const order = [];
  const map = new Map();
  rows.forEach((row) => {
    if (!map.has(row.product_id)) {
      order.push(row.product_id);
      map.set(row.product_id, {
        product_id: row.product_id,
        photo_url: row.photo_url,
        description: row.description,
        specification: row.specification_product,
        sample_qty: row.sample_qty || 0,
        entries: [],
      });
    }
    map.get(row.product_id).entries.push(row);
  });
  return order.map((id) => map.get(id));
}

function priceRangeLabel(entries) {
  const prices = entries.map((e) => e.suggested_price_usd).filter((v) => v != null);
  if (prices.length === 0) return "-";
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? `$${min.toFixed(2)}` : `$${min.toFixed(2)} - $${max.toFixed(2)}`;
}

export default function MobileCatalog() {
  const { t } = useTranslation();
  const { activeCart } = useCart();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // iter 284: three list views. "budget" is admin-only.
  const [view, setViewState] = useState(() => localStorage.getItem(VIEW_KEY) || "product");
  const effectiveView = view === "budget" && !isAdmin ? "product" : view;
  const setView = (v) => { localStorage.setItem(VIEW_KEY, v); setViewState(v); };
  const [filters, setFilters] = useState(_lastCatalog.filters || EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  useEffect(() => { api.get("/inventory/meta").then(({ data }) => setCategoryOptions(data.categories || [])).catch(() => {}); }, []);
  const filterCount = activeFilterCount(filters);
  // iter 265: keep the last result across tab switches so the list paints
  // instantly on return instead of a blank screen while refetching.
  const [query, setQuery] = useState(_lastCatalog.query);
  const [rows, setRows] = useState(_lastCatalog.rows);
  const [loading, setLoading] = useState(_lastCatalog.rows.length === 0);
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  // iter 275: 跟随当前报价单仓库;有仓库时默认开(用户关掉后记住)。
  const [onlySamples, setOnlySamples] = useState(() => {
    const stored = localStorage.getItem("mobileOnlySamples");
    return stored === null ? null : stored === "1";
  });
  const cartWarehouseId = activeCart?.sample_warehouse_id || null;
  const onlySamplesOn = onlySamples === null ? !!cartWarehouseId : onlySamples;
  const toggleOnlySamples = () => { const v = !onlySamplesOn; setOnlySamples(v); localStorage.setItem("mobileOnlySamples", v ? "1" : "0"); };
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // iter 216 (2026-02): 相机扫码。扫到 UPC → 填搜索框;若刚好命中 1 条产品,
  // 自动打开 ProductSheet 让用户直接选尺寸下单(见下方 pendingScan effect)。
  const [scannerOpen, setScannerOpen] = useState(false);
  // iter 217 (2026-02): 连扫模式。长按扫码按钮 500ms 进入,每扫一码直接
  // 加当前 Quotation,不打开 ProductSheet。状态文本给顶部绿色 banner。
  const [continuousMode, setContinuousMode] = useState(false);
  const [scannerStatus, setScannerStatus] = useState("");
  const [scanCount, setScanCount] = useState(0);
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);
  const statusClearTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    const params = { page_size: 200, ...buildInventoryParams(filters) };
    if (query.trim()) params.q = query.trim();
    if (onlySamplesOn && !filters.has_samples) { params.has_samples = true; if (cartWarehouseId && !filters.sample_warehouse_id) params.sample_warehouse_id = cartWarehouseId; }
    if (activeCart?.id) params.active_cart_id = activeCart.id;  // iter 277: own CI reservations not deducted
    const timer = setTimeout(() => {
      api.get("/inventory", { params })
        .then(({ data }) => {
          if (cancelled) return;
          const items = data.items || [];
          setRows(items);
          _lastCatalog.rows = items; _lastCatalog.query = query; _lastCatalog.filters = filters;
        })
        .catch(() => { if (!cancelled) { setRows([]); setLoadError(true); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, reloadTick, onlySamplesOn, cartWarehouseId, activeCart?.id, filters]);

  const products = useMemo(() => groupByProduct(rows), [rows]);

  // iter 284: admin approval toggle from the Budget view (same endpoint as desktop; FM sync within 1 min).
  const handleToggleApproval = async (entry) => {
    const prev = entry.approval;
    const next = (prev || "").toLowerCase() === "yes" ? "no" : "yes";
    const patch = (val) => setRows((rs) => rs.map((r) => (r.entry_id === entry.entry_id ? { ...r, approval: val } : r)));
    patch(next);
    try {
      const { data } = await api.post(`/inventory/entries/${entry.entry_id}/toggle-approval`);
      patch(data.approval);
    } catch (e) {
      patch(prev);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("mobile.approval_failed"));
    }
  };

  // iter 216 (2026-02): 扫码后若结果只有 1 条产品,自动打开 ProductSheet。
  // ⚠️ 竞态修复:setQuery 触发 250ms 防抖 → fetch → setLoading 循环。effect
  // 若立刻检查 products 会读到旧值。改用 `pendingScan` 记录待处理的扫码 code,
  // 只在 query === pendingScan 且 loading→false 完成一整轮抓取后才判定命中数。
  const [pendingScan, setPendingScan] = useState(null);
  useEffect(() => {
    if (!pendingScan) return;
    if (query !== pendingScan) return;   // debounce 还在等,或者 query 已被覆盖
    if (loading) return;                 // 正在 fetch,等下一次 render
    if (products.length === 1) {
      setSelectedProduct(products[0]);
      toast.success(`Scanned: ${(products[0].description || "product").substring(0, 40)}`);
    } else if (products.length === 0) {
      toast.error("No product matches this barcode");
    } else {
      toast.info(`${products.length} products match. Please pick one.`);
    }
    setPendingScan(null);
  }, [pendingScan, query, loading, products]);

  const handleScan = (code) => {
    const normalized = String(code || "").trim();
    if (!normalized) return;
    // iter 217: 连扫模式走独立通道 —— 直接查 UPC → 命中就加入报价,不动
    // 列表 filter,也不关浮层,不让 UI 抖动打断连续扫码节奏。
    if (continuousMode) {
      handleContinuousScan(normalized);
      return;
    }
    setScannerOpen(false);
    handleSingleScan(normalized);
  };

  // iter 270 (2026-06): 单扫直接走索引精确查询 /inventory/by-upc(<50ms),
  // 不再经过 250ms 防抖 + 30 字段正则搜索。查不到才回退到旧的全文搜索。
  const handleSingleScan = async (code) => {
    try {
      const { data } = await api.get("/inventory/by-upc", { params: { code } });
      const hits = groupByProduct(data.items || []);
      if (hits.length === 1) {
        setSelectedProduct(hits[0]);
        toast.success(`Scanned: ${(hits[0].description || "product").substring(0, 40)}`);
        return;
      }
      if (hits.length > 1) {
        setRows(data.items);
        toast.info(`${hits.length} products match. Please pick one.`);
        return;
      }
    } catch {}
    setLoading(true);
    setQuery(code);
    setPendingScan(code);
  };

  // iter 217: 连扫回调。独立 API 调用,不动 catalog 状态。
  const handleContinuousScan = async (code) => {
    if (!activeCart) {
      flashStatus("error", "先选一个 Quotation 才能连扫");
      return;
    }
    try {
      const { data } = await api.get("/inventory/by-upc", { params: { code } });
      const items = data.items || [];
      const ids = Array.from(new Set(items.map((r) => r.product_id)));
      if (ids.length === 0) {
        flashStatus("error", `未找到:${code}`);
      } else if (ids.length > 1) {
        flashStatus("warn", `${ids.length} 个产品匹配 ${code},请精确扫码或改单扫`);
      } else {
        const product = items.find((r) => r.product_id === ids[0]) || {};
        await api.post(`/carts/${activeCart.id}/add-product`, { product_id: ids[0] });
        const label = (product.description || product.item_no || "product").substring(0, 40);
        flashStatus("ok", `✓ 已加入:${label}`);
        setScanCount((n) => n + 1);
      }
    } catch (e) {
      flashStatus("error", "加入报价失败,请重试");
    }
  };

  const flashStatus = (_kind, text) => {
    setScannerStatus(text);
    clearTimeout(statusClearTimer.current);
    statusClearTimer.current = setTimeout(() => setScannerStatus(""), 2200);
  };

  // iter 217: 长按 500ms 开始连扫;短按开单扫。pointer 而不是 click,
  // 因为移动 Safari 上 click 事件会在 pointerup 之后再触发,冲突。
  const beginPress = () => {
    longPressFired.current = false;
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setContinuousMode(true);
      setScanCount(0);
      setScannerStatus("");
      setScannerOpen(true);
      if (navigator.vibrate) try { navigator.vibrate(30); } catch {}
    }, 500);
  };
  const endPress = () => {
    clearTimeout(longPressTimer.current);
    // 若长按已触发,pointerup 只是抬手,不再当短按处理
    if (longPressFired.current) return;
    setContinuousMode(false);
    setScannerOpen(true);
  };
  const cancelPress = () => {
    clearTimeout(longPressTimer.current);
    longPressFired.current = false;
  };

  const closeScanner = () => {
    setScannerOpen(false);
    if (continuousMode && scanCount > 0) {
      toast.success(`连扫模式:共加入 ${scanCount} 个产品到 "${activeCart?.name || "quotation"}"`);
    }
    setContinuousMode(false);
    setScanCount(0);
    setScannerStatus("");
  };

  return (
    <div data-testid="mobile-catalog-screen">
      <MobileHeader title={t("mobile.catalog")} subtitle={`${products.length} ${t("mobile.products")}`} />

      <button
        onClick={() => setSwitcherOpen(true)}
        data-testid="mobile-catalog-active-quote-pill"
        className="press-scale mx-4 mt-3 w-[calc(100%-32px)] flex items-center gap-2.5 bg-[#1E3A8A]/6 border border-[#1E3A8A]/15 rounded-full px-4 py-2 transition-colors duration-200"
      >
        <Receipt size={16} weight="duotone" className="text-[#1E3A8A] flex-shrink-0" />
        <span className="text-sm font-medium text-[#1E3A8A] truncate flex-1 text-left">
          {activeCart ? t("mobile.adding_to", { name: activeCart.name }) : t("mobile.select_quote")}
        </span>
        <CaretRight size={14} className="text-[#1E3A8A] flex-shrink-0" />
      </button>

      <div className="px-4 pt-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#78716C]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder=""
              data-testid="mobile-catalog-search-input"
              className="h-11 pl-11 rounded-[12px] border-[#E7E5E4] bg-white text-base"
            />
          </div>
          {/* iter 216 + 217: 扫码按钮。短按 = 单扫;长按 500ms = 连扫模式。 */}
          <button
            onPointerDown={beginPress}
            onPointerUp={endPress}
            onPointerCancel={cancelPress}
            onPointerLeave={cancelPress}
            onContextMenu={(e) => e.preventDefault()}
            data-testid="mobile-catalog-scan-button"
            aria-label="Scan barcode (long press for rapid mode)"
            title="Tap: 扫单个 · Hold: 连扫加入报价"
            className="press-scale h-11 w-11 flex-shrink-0 rounded-[12px] bg-[#1E3A8A] text-white flex items-center justify-center shadow-sm active:bg-[#152C6C] transition-colors select-none touch-none relative"
          >
            <BarcodeIcon size={22} weight="duotone" />
            {/* 长按提示小徽标 */}
            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-amber-400 text-black text-[8px] font-bold flex items-center justify-center border border-white shadow-sm">
              ⏱
            </span>
          </button>
          {/* iter 284: hidden filters — opens a sheet with the same filter fields as desktop. */}
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            data-testid="mobile-catalog-filter-button"
            aria-label="Filters"
            className={`press-scale h-11 w-11 flex-shrink-0 rounded-[12px] border flex items-center justify-center relative transition-colors ${filterCount > 0 ? "bg-[#1E3A8A]/8 border-[#1E3A8A]/30 text-[#1E3A8A]" : "bg-white border-[#E7E5E4] text-[#78716C]"}`}
          >
            <Funnel size={20} weight={filterCount > 0 ? "fill" : "regular"} />
            {filterCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#1E3A8A] text-white text-[10px] font-bold flex items-center justify-center border border-white" data-testid="mobile-catalog-filter-badge">{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* iter 284: view switcher. Budget view is admin-only. */}
      <div className="px-4 pt-2.5">
        <div className="grid grid-flow-col auto-cols-fr bg-[#F1F0EE] rounded-[12px] p-1" data-testid="mobile-catalog-view-switcher" role="tablist">
          {[["product", t("mobile.view_product")], ...(isAdmin ? [["budget", t("mobile.view_budget")]] : []), ["ci", t("mobile.view_ci")]].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={effectiveView === id} onClick={() => setView(id)} data-testid={`mobile-catalog-view-${id}`}
              className={`h-9 rounded-[9px] text-xs font-semibold transition-colors ${effectiveView === id ? "bg-white text-[#1E3A8A] shadow-sm" : "text-[#78716C]"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-2 flex justify-end">
        <button type="button" onClick={toggleOnlySamples} data-testid="mobile-only-samples-toggle"
          className={`press-scale text-xs px-3 py-1.5 rounded-full border ${onlySamplesOn ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#78716C] border-[#E7E5E4]"}`}>
          {cartWarehouseId ? t("samples.only_this_warehouse", { name: activeCart.sample_warehouse_name }) : t("samples.only_with_stock")}
        </button>
      </div>
      <div className="px-4 pt-2 pb-4">
        {loading && products.length === 0 && (
          <div className="bg-white rounded-[14px] border border-[#E7E5E4] overflow-hidden" data-testid="mobile-catalog-skeleton">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#F1F0EE] last:border-b-0 animate-pulse">
                <div className="w-14 h-14 rounded-[10px] bg-[#F1F0EE] flex-shrink-0" />
                <div className="flex-1 space-y-2"><div className="h-3 w-24 bg-[#F1F0EE] rounded" /><div className="h-3 w-40 bg-[#F1F0EE] rounded" /></div>
                <div className="h-3 w-12 bg-[#F1F0EE] rounded" />
              </div>
            ))}
          </div>
        )}
        {!loading && loadError && (
          <div className="text-center py-16 rounded-[16px] border border-dashed border-[#E7E5E4]" data-testid="mobile-catalog-error-state">
            <p className="text-sm text-[#78716C] mb-3">{t("mobile.load_failed")}</p>
            <button type="button" onClick={() => setReloadTick((n) => n + 1)} className="press-scale text-sm font-semibold text-[#1E3A8A]" data-testid="mobile-catalog-retry">{t("mobile.retry")}</button>
          </div>
        )}
        {!loading && !loadError && products.length === 0 && (
          <div className="text-center py-16 rounded-[16px] border border-dashed border-[#E7E5E4]" data-testid="mobile-catalog-empty-state">
            <Package size={30} weight="duotone" className="text-[#78716C] mx-auto mb-2" />
            <p className="text-sm text-[#78716C]">{t("mobile.no_products")}</p>
          </div>
        )}
        {products.length > 0 && (
          <div className="bg-white rounded-[14px] border border-[#E7E5E4] overflow-hidden" data-testid="mobile-catalog-list">
            {products.map((p) => (
              <CatalogRow key={p.product_id} p={p} t={t} view={effectiveView} isAdmin={isAdmin} onToggleApproval={handleToggleApproval} onClick={() => setSelectedProduct(p)} />
            ))}
          </div>
        )}
      </div>

      <MobileFilterSheet
        open={filterOpen}
        onOpenChange={setFilterOpen}
        filters={filters}
        setFilters={setFilters}
        categoryOptions={categoryOptions}
        warehouseScope={cartWarehouseId ? { id: cartWarehouseId, name: activeCart?.sample_warehouse_name } : null}
      />

      <ProductSheet
        product={selectedProduct}
        open={!!selectedProduct}
        onOpenChange={(v) => !v && setSelectedProduct(null)}
        onNeedQuote={() => setSwitcherOpen(true)}
      />
      <QuoteSwitcherSheet open={switcherOpen} onOpenChange={setSwitcherOpen} />
      <BarcodeScannerSheet
        open={scannerOpen}
        onClose={closeScanner}
        onScan={handleScan}
        continuous={continuousMode}
        statusText={scannerStatus}
      />
    </div>
  );
}
